/**
 * AiCompanionContext — Realtime-Subscription auf ai_suggestions.
 *
 * Provider montiert sich oberhalb des AuthProviders nicht möglich, weil
 * wir orgId brauchen — wird daher direkt unter AuthProvider montiert.
 * Held offen, solange ein eingeloggter User mit orgId existiert.
 *
 * Verantwortlichkeit:
 *   - Initial-Load der Suggestions via React Query
 *   - Realtime-Channel auf public.ai_suggestions, gefiltert nach
 *     organization_id; invalidiert die React-Query-Cache-Keys, sodass
 *     UI-Komponenten (InsightBell, AiSuggestionsInbox) automatisch
 *     aktualisieren
 *   - Resolve-Aktion via suggestion-resolve Edge Function
 *
 * Bewusst NICHT verantwortlich:
 *   - Per-kind-Rendering (lebt in components/ai/*)
 *   - Feature-Gating (lebt im Mount-Punkt, siehe Layout.tsx)
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { DEMO_AI_SUGGESTIONS } from '@/lib/demoData';
import type {
  AiSuggestion,
  AiSuggestionStatus,
} from '@/lib/ai/types';

interface AiCompanionContextValue {
  /** Alle pending+shown Suggestions für die aktuelle Org, sortiert
   *  nach priority desc, created_at desc. */
  suggestions: AiSuggestion[];
  /** Anzahl pending (für Badge-Counter). */
  pendingCount: number;
  isLoading: boolean;
  isError: boolean;
  /** Optimistic resolve. Bei Fehler rollback + toast. */
  resolve: (
    suggestion: AiSuggestion,
    action: 'accepted' | 'dismissed',
    resolvedAction?: string,
  ) => Promise<void>;
}

const Ctx = createContext<AiCompanionContextValue | null>(null);

const QK = (orgId: string | null) => ['ai-suggestions', orgId] as const;

const PRIORITY_RANK: Record<AiSuggestion['priority'], number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

export const AiCompanionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { profile } = useAuth();
  const { isDemo } = useDemo();
  const orgId = profile?.organization_id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QK(orgId),
    enabled: !!orgId,
    staleTime: 30_000,
    queryFn: async (): Promise<AiSuggestion[]> => {
      if (!orgId) return [];
      // Demo-Modus: rein client-seitige Mocks, kein DB-Roundtrip.
      // Damit erscheint die KI-Inbox sofort gefüllt — Partner-Demos
      // sehen die proaktive Plattform ohne erst Daten eintippen zu müssen.
      if (isDemo) {
        return DEMO_AI_SUGGESTIONS as unknown as AiSuggestion[];
      }
      // The generated supabase types do not yet know about ai_suggestions
      // (added in migration 20260517000006). Cast to any until
      // `pnpm supabase gen types` is re-run after deploy.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const table = (supabase.from('ai_suggestions' as any) as any);
      const { data, error } = await table
        .select(
          'id, org_id, patient_id, kind, priority, payload, source_run_id, ' +
            'dedup_key, status, created_at, shown_at, expires_at, ' +
            'resolved_at, resolved_by, resolved_action',
        )
        .eq('org_id', orgId)
        .in('status', ['pending', 'shown'])
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as AiSuggestion[];
    },
  });

  // Realtime: invalidate on any change in the org's suggestions table.
  // Im Demo-Modus überspringen — wir nutzen client-Mocks ohne DB.
  useEffect(() => {
    if (!orgId || isDemo) return;
    const channel = supabase
      .channel(`ai-suggestions-${orgId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ai_suggestions',
          filter: `org_id=eq.${orgId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: QK(orgId) });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgId, isDemo, queryClient]);

  const resolve = useCallback<AiCompanionContextValue['resolve']>(
    async (suggestion, action, resolvedAction) => {
      if (!orgId) return;

      // Optimistic: drop the row from the cached list (it will reappear
      // as 'accepted'/'dismissed' if the rollback path fires).
      const prev = queryClient.getQueryData<AiSuggestion[]>(QK(orgId)) ?? [];
      queryClient.setQueryData<AiSuggestion[]>(
        QK(orgId),
        prev.filter((s) => s.id !== suggestion.id),
      );

      // Demo-Modus: nur lokale Optimistic-Entfernung, kein Edge-Call.
      if (isDemo) {
        // Demo state lives only in React-Query cache; resolved
        // suggestions stay gone until the component unmounts.
        return;
      }

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) throw new Error('keine aktive Session');

        const supabaseUrl =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (supabase as any)?.supabaseUrl ?? '';
        const res = await fetch(`${supabaseUrl}/functions/v1/suggestion-resolve`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            suggestion_id: suggestion.id,
            action,
            resolved_action: resolvedAction ?? null,
          }),
        });
        if (!res.ok) {
          const errText = await res.text();
          throw new Error(`resolve failed: ${res.status} ${errText.slice(0, 200)}`);
        }
      } catch (e) {
        // Rollback optimistic update
        queryClient.setQueryData<AiSuggestion[]>(QK(orgId), prev);
        toast.error(
          `KI-Vorschlag konnte nicht bearbeitet werden: ${
            e instanceof Error ? e.message : String(e)
          }`,
        );
      } finally {
        queryClient.invalidateQueries({ queryKey: QK(orgId) });
      }
    },
    [orgId, isDemo, queryClient],
  );

  const value = useMemo<AiCompanionContextValue>(() => {
    const list = (query.data ?? []).slice().sort((a, b) => {
      const pa = PRIORITY_RANK[a.priority] ?? 9;
      const pb = PRIORITY_RANK[b.priority] ?? 9;
      if (pa !== pb) return pa - pb;
      return b.created_at.localeCompare(a.created_at);
    });
    return {
      suggestions: list,
      pendingCount: list.filter((s: AiSuggestion) => statusIsPending(s.status))
        .length,
      isLoading: query.isLoading,
      isError: query.isError,
      resolve,
    };
  }, [query.data, query.isLoading, query.isError, resolve]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useAiCompanion(): AiCompanionContextValue {
  const v = useContext(Ctx);
  if (!v) {
    // Safe fallback: keep components mountable even if the provider is
    // not in the tree (e.g. in storybook or on public routes). Returns
    // an empty state with a no-op resolve.
    return {
      suggestions: [],
      pendingCount: 0,
      isLoading: false,
      isError: false,
      resolve: async () => {},
    };
  }
  return v;
}

function statusIsPending(s: AiSuggestionStatus): boolean {
  return s === 'pending' || s === 'shown';
}
